import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { useGeolocation } from "@/hooks/useGeolocation";
import React from "react";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, Zap, X, Search, Check, ChevronsUpDown, AlertCircle } from "lucide-react";
import { z } from 'zod';
import { supabase } from "@/integrations/supabase/client";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './AccessModal.css';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Flag } from "@/utils/flag";
import { COUNTRIES } from "@/utils/countries";

const requestAccessSchema = z.object({
  fullName: z.string()
    .trim()
    .min(3, 'Full name must be at least 3 characters')
    .max(100, 'Full name must be less than 100 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Name contains invalid characters')
    .refine(
      (name) => name.trim().split(/\s+/).length >= 2,
      'Please enter your full name (first and last name)'
    ),
  email: z.string()
    .trim()
    .toLowerCase()
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters')
    .refine(
      (email) => {
        const parts = email.split('@');
        if (parts.length !== 2) return false;
        const domain = parts[1];
        return domain.includes('.') && domain.split('.').pop()!.length >= 2;
      },
      'Email must have a valid domain (e.g., example.com)'
    ),
  phone: z.string()
    .refine(
      (value) => {
        if (!value) return true; // Optional field
        try {
          return isValidPhoneNumber(value);
        } catch {
          return false;
        }
      },
      'Please enter a valid phone number'
    )
    .optional(),
  reason: z.string()
    .trim()
    .min(10, 'Please provide at least 10 characters')
    .max(500, 'Reason must be less than 500 characters')
    .optional()
});

interface AccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AccessModal = ({ open, onOpenChange }: AccessModalProps) => {
  const { country: detectedCountry } = useGeolocation();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    reason: ""
  });
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateField = (fieldName: keyof typeof formData) => {
    try {
      const fieldSchema = requestAccessSchema.shape[fieldName as keyof typeof requestAccessSchema.shape];
      if (fieldSchema) {
        fieldSchema.parse(formData[fieldName]);
        setValidationErrors(prev => ({ ...prev, [fieldName]: '' }));
        setErrors(prev => ({ ...prev, [fieldName]: '' }));
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors[0]?.message || 'Invalid value';
        setValidationErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
        setErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setValidationErrors({});
    
    // Validate inputs
    const result = requestAccessSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      setValidationErrors(fieldErrors);
      toast.error(result.error.errors[0].message);
      return;
    }

    setLoading(true);
    
    try {
      // Split full name into first and last name
      const nameParts = result.data.fullName.trim().split(/\s+/);
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      // Collect device metadata
      const userAgent = navigator.userAgent;
      const getBrowser = () => {
        if (userAgent.includes('Firefox')) return 'Firefox';
        if (userAgent.includes('Edg')) return 'Edge';
        if (userAgent.includes('Chrome')) return 'Chrome';
        if (userAgent.includes('Safari')) return 'Safari';
        return 'Unknown';
      };
      const getDeviceType = () => {
        if (/mobile/i.test(userAgent)) return 'Mobile';
        if (/tablet/i.test(userAgent)) return 'Tablet';
        return 'Desktop';
      };

      // Attempt to get IP via public API
      let ipAddress = 'Unknown';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        ipAddress = ipData.ip || 'Unknown';
      } catch {
        // ignore
      }

      // Check for duplicate email/phone before submitting
      const { data: dupCheck, error: dupError } = await supabase.functions.invoke('check-duplicate-signup', {
        body: { email: result.data.email, phone: result.data.phone || null }
      });

      if (dupError) {
        console.error('Duplicate check failed:', dupError);
        throw new Error('Could not verify your details. Please try again.');
      }

      if (dupCheck?.duplicate) {
        toast.error(dupCheck.message);
        setLoading(false);
        return;
      }

      // Submit via edge function (bypasses RLS)
      const { data: submitData, error: submitError } = await supabase.functions.invoke('submit-access-request', {
        body: {
          firstName,
          lastName,
          email: result.data.email,
          phone: result.data.phone || null,
          reason: result.data.reason || null,
          ipAddress,
          userAgent,
        }
      });

      if (submitError) {
        console.error('Submit error:', submitError);
        throw new Error('Failed to submit access request');
      }

      if (submitData?.error) {
        toast.error(submitData.error);
        return;
      }

      // Fire-and-forget: send emails in background (don't block UI)
      supabase.functions.invoke('send-access-request-emails', {
        body: {
          firstName,
          lastName,
          email: result.data.email,
          phone: result.data.phone,
          reason: result.data.reason,
          ipAddress,
          userAgent,
          deviceType: getDeviceType(),
          browser: getBrowser(),
          requestId: submitData?.data?.[0]?.id,
        }
      }).then(({ error }) => {
        if (error) console.error('Error sending emails:', error);
      });

      toast.success('Access request submitted! Check your email.');

      setSubmittedEmail(result.data.email);
      setShowSuccess(true);
      setFormData({ fullName: "", email: "", phone: "", reason: "" });
    } catch (error) {
      console.error('Error submitting access request:', error);
      toast.error('Failed to submit access request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!showSuccess) {
      onOpenChange(false);
      setFormData({ fullName: "", email: "", phone: "", reason: "" });
      setErrors({});
      setValidationErrors({});
    }
  };

  return (
    <>
      <Dialog open={open && !showSuccess} onOpenChange={handleClose}>
        <DialogContent className="border border-border/60 text-foreground w-full max-w-[calc(100vw-24px)] sm:max-w-[calc(100vw-32px)] md:max-w-md p-0 max-h-[90vh] overflow-y-auto overflow-x-visible rounded-2xl" style={{ background: 'hsl(0 0% 6%)' }}>
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 text-muted-foreground hover:text-foreground transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="px-6 sm:px-8 pt-7 sm:pt-8 pb-0">
            <DialogHeader className="space-y-2 relative">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[10px] uppercase tracking-[0.35em] text-gold/60 font-medium">Private membership</p>
              </div>
              <DialogTitle className="text-xl font-semibold text-foreground tracking-tight">
                Request access
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                We onboard approved clients within 24-48 hours
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Divider */}
          <div className="mx-6 sm:mx-8 my-5 h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />

          <div className="px-6 sm:px-8 pb-7 sm:pb-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Full Name Field */}
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                  Full name <span className="text-gold ml-0.5">*</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  autoComplete="name"
                  inputMode="text"
                  spellCheck={false}
                  className={`w-full rounded-md bg-background/50 border ${
                    errors.fullName ? 'border-destructive/40 ring-1 ring-destructive/30' : 'border-border/50'
                  } px-4 py-3 text-foreground outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-all placeholder:text-muted-foreground/30 h-11 text-sm`}
                  style={{ fontSize: '16px', caretColor: 'hsl(var(--gold))' }}
                  placeholder="First and last name"
                  value={formData.fullName}
                  onChange={(e) => {
                    const value = e.target.value.replace(/^\s+/, '').replace(/[0-9]/g, '');
                    setFormData({ ...formData, fullName: value });
                    setErrors({ ...errors, fullName: "" });
                  }}
                  required
                />
                {errors.fullName && (
                  <p className="text-[11px] text-destructive px-0.5">{errors.fullName}</p>
                )}
              </div>

              {/* Email Field */}
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                  Email <span className="text-gold ml-0.5">*</span>
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    spellCheck={false}
                    className={`w-full rounded-md bg-background/50 border px-4 py-3 pr-10 text-foreground outline-none transition-all placeholder:text-muted-foreground/30 h-11 text-sm ${
                      validationErrors.email ? 'border-destructive/40 ring-1 ring-destructive/30 focus:border-destructive/50' : 'border-border/50 focus:border-gold/50 focus:ring-1 focus:ring-gold/20'
                    }`}
                    style={{ fontSize: '16px', caretColor: 'hsl(var(--gold))' }}
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => {
                      const value = e.target.value.toLowerCase().replace(/^\s+/, '');
                      setFormData({ ...formData, email: value });
                      if (validationErrors.email) {
                        setValidationErrors({ ...validationErrors, email: "" });
                        setErrors({ ...errors, email: "" });
                      }
                    }}
                    onBlur={() => validateField('email')}
                    required
                  />
                  {validationErrors.email ? (
                    <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                  ) : formData.email.includes('@') && formData.email.includes('.') ? (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                  ) : null}
                </div>
                {validationErrors.email && (
                  <p className="text-[11px] text-destructive px-0.5 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.email}
                  </p>
                )}
              </div>

              {/* Phone Field */}
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                  Phone <span className="text-muted-foreground/40 text-[10px] normal-case tracking-normal ml-1">(optional)</span>
                </label>
                <div className="relative">
                  <PhoneInput
                    international
                    defaultCountry={(detectedCountry || 'US') as any}
                    value={formData.phone}
                    onChange={(value) => {
                      if (!value || value.replace(/\D/g, '').length <= 15) {
                        setFormData({ ...formData, phone: value || '' });
                        if (validationErrors.phone) {
                          setValidationErrors({ ...validationErrors, phone: "" });
                          setErrors({ ...errors, phone: "" });
                        }
                      }
                    }}
                    onBlur={() => validateField('phone')}
                    countrySelectComponent={({ value, onChange, options, disabled }) => {
                      const [triggerRef, setTriggerRef] = React.useState<HTMLButtonElement | null>(null);
                      const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>({});

                      React.useEffect(() => {
                        if (!triggerRef) return;

                        const updateDropdownHeight = () => {
                          const rect = triggerRef.getBoundingClientRect();
                          const viewportHeight = window.innerHeight;
                          const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom') || '0');
                          
                          const spaceBelow = viewportHeight - rect.bottom - 8 - safeAreaBottom;
                          const spaceAbove = rect.top - 8 - safeAreaBottom;
                          
                          const maxHeight = Math.max(180, Math.min(viewportHeight * 0.6, spaceBelow));
                          
                          setDropdownStyle({
                            '--dropdown-max': `${maxHeight}px`,
                          } as React.CSSProperties);
                        };

                        updateDropdownHeight();
                        window.addEventListener('resize', updateDropdownHeight);
                        window.addEventListener('scroll', updateDropdownHeight, true);
                        
                        return () => {
                          window.removeEventListener('resize', updateDropdownHeight);
                          window.removeEventListener('scroll', updateDropdownHeight, true);
                        };
                      }, [triggerRef]);

                      return (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            ref={setTriggerRef}
                            type="button"
                            disabled={disabled}
                            className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-gold/10 transition-all duration-200 border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-gold/30"
                          >
                            <Flag code={(value as string) || "GB"} size={20} className="shadow-[0_0_0_1px_hsl(var(--gold)/.25)]" />
                            <ChevronsUpDown className="h-3 w-3 text-gold/70" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[calc(100vw-32px)] sm:w-[320px] p-0 border border-border/60 backdrop-blur-xl shadow-2xl overflow-hidden phone-dropdown-container"
                          style={{ ...dropdownStyle, background: 'hsl(0 0% 6%)' }}
                          side="bottom"
                          align="start"
                          sideOffset={8}
                          onWheel={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                        >
                          <Command className="bg-transparent" filter={(value, search) => {
                            const country = COUNTRIES.find(c => c.code.toLowerCase() === value.toLowerCase());
                            if (!country) return 0;
                            const searchLower = search.toLowerCase();
                            if (country.label.toLowerCase().includes(searchLower)) return 1;
                            if (country.code.toLowerCase().includes(searchLower)) return 1;
                            if (country.dialCode.includes(searchLower)) return 1;
                            return 0;
                          }}>
                            <div className="flex items-center border-b border-border/40 px-3" style={{ background: 'hsl(0 0% 4%)' }}>
                              <Search className="mr-2 h-4 w-4 shrink-0 text-gold/60" />
                              <CommandInput 
                                placeholder="Search by name, code, or +44..." 
                                className="h-11 bg-transparent border-0 text-foreground placeholder:text-muted-foreground/40 focus:ring-0"
                              />
                            </div>
                            <CommandList 
                              className="overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-gold/20 scrollbar-track-transparent phone-dropdown-scroll"
                              style={{ maxHeight: 'var(--dropdown-max, 256px)' }}
                            >
                              <CommandEmpty className="py-8 text-center text-sm text-muted-foreground">
                                No country found.
                              </CommandEmpty>
                              <CommandGroup className="p-1.5">
                                {COUNTRIES.map((country) => (
                                  <CommandItem
                                    key={country.code}
                                    value={country.code}
                                    onSelect={() => {
                                      onChange(country.code);
                                    }}
                                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg hover:bg-gold/10 text-foreground aria-selected:bg-gold/15 transition-all duration-200 group"
                                  >
                                    <Flag code={country.code} size={22} />
                                    <div className="flex-1 flex items-center justify-between min-w-0">
                                      <span className="text-sm font-medium truncate">{country.label}</span>
                                      <span className="text-xs text-gold/70 font-mono ml-2 flex-shrink-0">{country.dialCode}</span>
                                    </div>
                                    <Check
                                      className={`h-4 w-4 text-gold transition-opacity ${value === country.code ? "opacity-100" : "opacity-0"}`}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                          </PopoverContent>
                        </Popover>
                      );
                    }}
                    className={`phone-input-luxury ${validationErrors.phone ? 'phone-error' : ''}`}
                  />
                  {formData.phone && (
                    <div className="absolute right-3 top-3.5 z-10">
                      {validationErrors.phone ? (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      ) : formData.phone && isValidPhoneNumber(formData.phone) ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : formData.phone ? (
                        <AlertCircle className="h-4 w-4 text-amber-400" />
                      ) : null}
                    </div>
                  )}
                </div>
                {validationErrors.phone && (
                  <p className="text-[11px] text-destructive px-0.5 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.phone}
                  </p>
                )}
              </div>

              {/* Reason Field */}
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                  Why join? <span className="text-muted-foreground/40 text-[10px] normal-case tracking-normal ml-1">(optional)</span>
                </label>
                <textarea
                  id="reason"
                  className={`w-full rounded-md bg-background/50 border ${
                    errors.reason ? 'border-destructive/40 ring-1 ring-destructive/30' : 'border-border/50'
                  } px-4 py-3 text-foreground text-sm outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-all min-h-[100px] resize-none placeholder:text-muted-foreground/30`}
                  placeholder="Tell us about yourself..."
                  value={formData.reason}
                  onChange={(e) => {
                    setFormData({ ...formData, reason: e.target.value });
                    setErrors({ ...errors, reason: "" });
                  }}
                />
                {errors.reason && (
                  <p className="text-[11px] text-destructive px-0.5">{errors.reason}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-1">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl font-medium text-[13px] tracking-[0.1em] uppercase relative overflow-hidden group transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.97]"
                  style={{
                    background: 'linear-gradient(135deg, hsl(var(--gold) / 0.1), hsl(var(--gold) / 0.03))',
                    border: '1px solid hsl(var(--gold) / 0.4)',
                    color: 'hsl(var(--gold))',
                    boxShadow: '0 0 24px -8px hsl(var(--gold) / 0.2), inset 0 1px 0 hsl(var(--gold) / 0.12)',
                  }}
                >
                  <span className="relative z-10">
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                        Submitting
                      </span>
                    ) : (
                      'Request access'
                    )}
                  </span>
                  {/* Inner ambient glow */}
                  <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{ background: 'linear-gradient(135deg, hsl(var(--gold) / 0.15), transparent)' }} />
                  {/* Shimmer */}
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent -translate-x-full animate-[shimmerSweep_3s_ease-in-out_infinite]" />
                </button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Popup — Editorial Luxury */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: 'hsl(var(--bg-app))' }}
          >
            {/* Ambient glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[350px] rounded-full bg-gold/[0.04] blur-[100px] pointer-events-none" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="border border-border/60 rounded-2xl max-w-md w-full relative overflow-hidden"
              style={{ background: 'hsl(0 0% 6%)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                onClick={() => {
                  setShowSuccess(false);
                  onOpenChange(false);
                }}
                className="absolute top-5 right-5 text-muted-foreground hover:text-foreground transition-colors z-10 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </motion.button>

              <div className="px-8 py-10 text-center">
                {/* Gold diamond icon */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
                  className="mb-7"
                >
                  <div className="w-20 h-20 mx-auto rounded-full border border-gold/30 flex items-center justify-center" style={{ background: 'hsl(var(--gold) / 0.08)' }}>
                    <CheckCircle2 className="w-10 h-10 text-gold" strokeWidth={1.5} />
                  </div>
                </motion.div>

                {/* Heading */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                >
                  <p className="text-[10px] uppercase tracking-[0.4em] text-gold/60 font-medium mb-3">Application received</p>
                  <h2 className="text-2xl font-semibold text-foreground tracking-tight mb-2">
                    Welcome aboard
                  </h2>
                  <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto">
                    Our exclusive concierge team will review your application and reach out within{" "}
                    <span className="text-gold font-semibold">24-48 hours</span>
                  </p>
                </motion.div>

                {/* Divider */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="w-20 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent mx-auto my-6"
                />

                {/* Email confirmation */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <p className="text-sm text-muted-foreground mb-5">
                    Confirmation sent to{" "}
                    <span className="text-foreground font-medium" style={{ overflowWrap: 'anywhere' }}>{submittedEmail}</span>
                  </p>

                  <div className="border border-border/40 rounded-xl px-5 py-4 mb-7" style={{ background: 'hsl(0 0% 4%)' }}>
                    <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                      <span className="text-gold text-xs">✦</span>
                      Check your inbox for confirmation
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setShowSuccess(false);
                      onOpenChange(false);
                    }}
                    className="px-10 py-3 rounded-xl font-medium text-[13px] tracking-[0.1em] uppercase relative overflow-hidden group transition-all duration-200"
                    style={{
                      background: 'linear-gradient(135deg, hsl(var(--gold) / 0.1), hsl(var(--gold) / 0.03))',
                      border: '1px solid hsl(var(--gold) / 0.4)',
                      color: 'hsl(var(--gold))',
                      boxShadow: '0 0 24px -8px hsl(var(--gold) / 0.2), inset 0 1px 0 hsl(var(--gold) / 0.12)',
                    }}
                  >
                    <span className="relative z-10">Got it</span>
                    <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{ background: 'linear-gradient(135deg, hsl(var(--gold) / 0.15), transparent)' }} />
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
